<!-- AssignVolunteerTeamDialog.vue -->
<template>
  <BaseDialog
    v-model="internalValue"
    @input="$emit('input', $event)"
    :title="$t('volunteers.assign_to_team')"
    :cancelText="$t('cancel')"
    :confirmText="$t('confirm')"
    :loading="loading"
    :maxWidth="'520px'"
    :backgroundColor="'#e9f4ff'"
    :confirmColor="'#1976d2'"
    :confirmDisabled="!selectedVolunteer"
    @cancel="onCancel"
    @confirm="handleConfirm"
  >
    <v-card v-if="teamPreview" flat class="team-card mb-3">
      <v-layout row no-wrap align-center>
        <v-flex shrink class="pl-2 pr-3 py-2">
          <v-avatar size="52" class="elevation-1 white team-avatar">
            <img
              :src="teamLogoSrc"
              alt=""
              role="presentation"
              @error="onLogoError"
            />
          </v-avatar>
        </v-flex>

        <v-flex grow class="py-2 pr-3">
          <div class="team-line team-name" :title="teamPreview.teamName || '-'">
            {{ teamPreview.teamName || '-' }}
          </div>
          <div class="team-line team-club grey--text text--darken-1" :title="teamPreview.clubName || '-'">
            {{ teamPreview.clubName || '-' }}
          </div>
          <div class="team-line team-category grey--text text--darken-2" :title="teamPreview.category || '-'">
            {{ teamPreview.category || '-' }}
          </div>
        </v-flex>
      </v-layout>
    </v-card>

    <v-form ref="form" v-model="formIsValid">
      <v-autocomplete
        v-model="selectedVolunteer"
        :items="volunteerItems"
        item-text="fullName"
        item-value="id"
        :loading="isLoading"
        :disabled="isLoading"
        :label="$t('volunteers.select_volunteer')"
        dense
        outlined
        clearable
        :rules="[requiredRule]"
        :filter="customFilter"
        :no-data-text="$t('no_results')"
      >
        <template v-slot:selection="{ item }">
          <v-avatar size="28" class="mr-2" color="#1976d2" v-if="item && item.initials">
            <span class="vol-avatar-text">{{ item.initials }}</span>
          </v-avatar>
          <span class="vol-sel-name">{{ item.fullName }}</span>
          <span v-if="item.city" class="ml-2 caption grey--text">• {{ item.city }}</span>
        </template>

        <template v-slot:item="{ item }">
          <v-list-tile-avatar>
            <v-avatar size="28" color="#1976d2">
              <span class="vol-avatar-text">{{ item.initials }}</span>
            </v-avatar>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              {{ item.fullName }}
              <span v-if="item.city" class="ml-1 caption grey--text">({{ item.city }})</span>
            </v-list-tile-title>
            <v-list-tile-sub-title>
              <span v-if="item.dni">{{ item.dni }}</span>
              <span v-if="item.dni && (item.phone || item.email)"> · </span>
              <span v-if="item.phone">{{ item.phone }}</span>
              <span v-if="item.phone && item.email"> · </span>
              <span v-if="item.email">{{ item.email }}</span>
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </template>
      </v-autocomplete>
    </v-form>
  </BaseDialog>
</template>

<script>
import BaseDialog from "@/components/ui/BaseDialog.vue";

export default {
  name: "AssignVolunteerTeamDialog",
  components: { BaseDialog },
  props: {
    value: { type: Boolean, required: true },
    loading: { type: Boolean, default: false },
    teamId: { type: String, required: true }, // not displayed
    teamName: { type: String, default: "" },
    clubName: { type: String, default: "" },
    category: { type: String, default: "" },
    team: { type: Object, default: null },
    shield: { type: String, default: "" },                 // <-- keep empty by default
    fallbackShield: { type: String, default: "/img/avatar.svg" } // <-- public path (exists)
  },
  data () {
    return {
      formIsValid: false,
      isLoading: false,
      selectedVolunteer: null,
      logoErrored: false
    }
  },
  computed: {
    internalValue: {
      get () { return this.value },
      set (val) { this.$emit("input", val) }
    },

    teamPreview () {
      const sanitize = this.sanitizeShield
      if (this.team) {
        return {
          teamName: this.team.name || this.teamName,
          clubName: (this.team.club && this.team.club.name) || this.clubName,
          category: (this.team.category && (this.team.category.name || this.team.category)) || this.category,
          shield: sanitize(this.team.shield || this.shield)
        }
      }
      return {
        teamName: this.teamName,
        clubName: this.clubName,
        category: this.category,
        shield: sanitize(this.shield)
      }
    },

    /** final img src: valid shield OR fallback */
    teamLogoSrc () {
      if (!this.logoErrored && this.teamPreview && this.teamPreview.shield) {
        return this.teamPreview.shield
      }
      return this.fallbackShield
    },

    confirmed_list () {
      return this.$store.getters['volunteers/getEventConfirmedList'];
    },

    volunteerItems () {
      const raw = this.confirmed_list
      const list = Array.isArray(raw) ? raw : Object.values(raw)

      const currentEventId = this.currentEventId

      const filtered = list.filter(v => {
        if (v.visible === false) return false
        if (!currentEventId) return true
        return Array.isArray(v.event)
          ? v.event.some(e => {
              const eid = Array.isArray(e.id) ? e.id : [e.id]
              return eid && eid.includes(currentEventId)
            })
          : true
      })

      return filtered.map(v => {
        const fullName = [v.name, v.surname].filter(Boolean).join(" ").trim() || v.email || v.id
        const initials =
          ((v.name && v.name[0]) || "").concat((v.surname && v.surname[0]) || "").toUpperCase() || "V"
        return {
          id: v.id,
          fullName,
          initials,
          city: v.city || null,
          phone: v.phone || null,
          email: v.email || null,
          dni: v.dni || null,
          raw: v
        }
      })
    },

    currentEventId () {
      const getCurrentEvent = this.$store.getters["events/getCurrentEvent"];
      const ev = typeof getCurrentEvent === "function" ? getCurrentEvent() : null;
      return ev && ev.id;
    }
  },
  watch: {
    internalValue (open) {
      if (open) {
        this.logoErrored = false;
        this.fetchVolunteers();
      } else {
        this.resetState();
      }
    }
  },
  methods: {
    resetState () {
      this.formIsValid = false;
      this.isLoading = false;
      this.selectedVolunteer = null;
      this.logoErrored = false;
    },

    requiredRule (v) {
      return !!v || this.$t("required_field");
    },

    onCancel () {
      if (this.loading) return;
      this.resetState();
      this.$emit("input", false);
    },

    async fetchVolunteers () {
      try {
        this.isLoading = true;
        const eventId = this.currentEventId;
        await this.$store.dispatch("volunteers/loadEventConfirmedList", { event: eventId });
      } catch (e) {
        console.log("Error fetching volunteers:", e);
      } finally {
        this.isLoading = false;
      }
    },

    customFilter (item, queryText) {
      const q = (queryText || "").toString().toLowerCase();
      if (!q) return true;
      const fields = [item.fullName, item.city, item.phone, item.email];
      return fields.some(f => (f || "").toString().toLowerCase().includes(q));
    },

    handleConfirm () {
      this.$refs.form.validate();
      if (!this.formIsValid) return;

      this.$emit("assign", {
        teamId: this.teamId,
        volunteerId: this.selectedVolunteer
      });
    },

    /** '', 'null', null => null so code picks fallback */
    sanitizeShield (u) {
      if (u === undefined || u === null) return null;
      const s = String(u).trim();
      if (!s || s.toLowerCase() === "null") return null;
      return s;
    },

    /** switch to fallback once; prevents loops */
    onLogoError (e) {
      if (this.logoErrored) return;
      this.logoErrored = true;
      if (e && e.target && e.target.src !== this.fallbackShield) {
        e.target.src = this.fallbackShield;
      }
    }
  }
}
</script>

<style scoped>
.team-card {
  background: #f5f7fb;
  border-radius: 8px;
}

.team-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.team-avatar img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  image-rendering: auto;
}

.team-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-name { font-weight: 700; font-size: 1rem; color: #1f2a37; line-height: 1.15; }
.team-club { font-weight: 600; font-size: 0.92rem; line-height: 1.2; }
.team-category { font-weight: 600; font-size: 0.88rem; line-height: 1.2; }

.vol-avatar-text { color: #fff; font-weight: 700; font-size: 0.85rem; }
.vol-sel-name { font-weight: 600; }
</style>
